/**
 * pb-result-strip.js — the arrows, the counter and the video behaviour of the PB Result strip.
 *
 * Ticket #11, second round. Read off the section root:
 *
 *   data-layout="pictures"  (prototype C) before-and-after cards, no video anywhere. The script
 *                           only runs the arrows and the counter.
 *   data-layout="video"     (prototype D) one 9:16 clip per card. Only the ACTIVE card — the one
 *                           in the middle of the strip — ever plays. Every other card, and the
 *                           active one while paused, shows its poster: a frame from the AFTER.
 *   data-start="autoplay"   the active card plays itself, muted and looping, while the strip is
 *                           on screen. Tapping it pauses and resumes. A visitor who asked the
 *                           system for reduced motion gets tap to play instead.
 *   data-start="tap"        nothing plays until the visitor taps the active card.
 *
 * The one rule both prototypes share, from Fran's verdict on the first round: **the strip only
 * moves when the visitor presses an arrow or swipes.** Tapping a card, playing one, pausing one
 * or a clip ending never scrolls it. So nothing in the playback half of this file calls
 * `scrollToIndex`, and a tap on any card other than the active one does nothing at all.
 *
 * Ticket #34, the endless loop (Fran, 15 September). The strip opens on card 1 in the middle,
 * with card 12 on its left and card 2 on its right; the arrows and swiping wrap (12 -> 1,
 * 1 -> 12), and the counter always names the card in the middle. How, without clones:
 *   - the cards stay in the page in their own order, 1 to 12, so a screen reader reads each card
 *     once and in order, and no poster or clip exists twice to be fetched twice
 *   - what moves is their flex `order`: `slotsFor` puts the active card in the middle slot, the
 *     five before it on its left and the six after it on its right (for twelve cards)
 *   - every time the strip comes to rest, `arrange` rotates the order around the new active card
 *     and moves `scrollLeft` by exactly what the card in the middle moved, so nothing on screen
 *     jumps and the playing clip is never touched. There is always a card on both sides.
 *   - one swipe moves one card (`scroll-snap-stop: always` in the section's stylesheet), so a
 *     long fling cannot run past the end of the row before the strip is re-arranged
 *   - nothing is re-arranged while a finger is on the strip
 * With fewer than LOOP_MIN cards there is nothing to wrap to, and the strip behaves as before:
 * the arrows stop at the ends.
 *
 * Ticket #41, the stutter (15 September), built on preview theme A (Website-files/previews/t41/)
 * and live on MAIN since ticket #51 (15 September; Fran picked A without asking Mia).
 * Measured on MAIN (media/qc-2026-09-15-t41/NOTES.md): the loop itself was clean (no long task, no
 * layout shift, the middle card never moved backwards), but on every arrow press
 *   - the new clip started 180 to 260 ms BEFORE its card reached the middle, so the poster swapped
 *     to the clip's first frame while the strip was still sliding
 *   - the outgoing clip lost its src 2 to 14 ms after the press, so the card sliding away snapped
 *     from the playing picture to its poster mid slide
 *   - and the clips themselves were 24 fps, shown on a 60 Hz screen as frames held for 2 and 3
 *     refreshes in turn (3:2 judder). That one is fixed in the files (30 fps), not here.
 * So here: the new clip is PREPARED at the press (src set, fetching during the slide) and STARTED
 * only at rest (`shouldStart`, `startMoving` / `stopMoving`, `settle`), once it can play through;
 * the outgoing clip pauses and fades out on its own last frame, and its src goes after the fade
 * (`stop(i, { fade })`, RELEASE_AFTER_MS). The loop, the counter and the arrows are unchanged.
 *
 * Why positions are measured with getBoundingClientRect and not offsetLeft: the first round used
 * `card.offsetLeft`, which is measured from the card's offsetParent. The strip is not
 * positioned, so that parent was <body> and every position was off by the strip's own distance
 * from the left edge of the page — 143 px at a 1440 px window, 383 px at 1920. Once that error
 * passed half a card (209 px) the snap landed one card further than asked: "next" skipped a card,
 * the counter named the card to the left of the one on screen, and tapping the card on screen
 * counted as tapping a *different* card, which scrolled the strip. Measuring both the card and
 * the strip in the same coordinate space removes the error at every width, and it also follows
 * the flex `order` of the loop, which offsetLeft on its own would not tell us anything about.
 *
 * Why the videos carry `data-src` instead of `src`: a `src` (and a `poster` attribute) is fetched
 * during parsing whatever `preload` says. The poster is an <img loading="lazy"> behind the video
 * instead, and the src is copied in only at the moment of play.
 */

const HAS_WINDOW = typeof window !== 'undefined';
const REDUCED_MOTION = HAS_WINDOW && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : { matches: false };
const HAS_SCROLLEND = HAS_WINDOW && 'onscrollend' in window;
const SETTLE_MS = 140; // only where the browser has no `scrollend`
const ARRIVED_PX = 2;

/* Ticket #41, the stutter fix (live on MAIN since #51). */
/** How long the outgoing clip fades out on its own last frame before its src is released. The
 *  section fades the video over .28 s; releasing at once blanked it to the poster mid slide. */
export const RELEASE_AFTER_MS = 320;
/** If the strip never reports coming to rest (no scroll happened, or no `scrollend`), start anyway. */
export const START_FAILSAFE_MS = 1200;
/** How long a clip may wait for `canplaythrough` before it is started regardless. Short, because
 *  iOS Safari may not buffer that far before `play()`; the poster covers the wait either way. */
export const READY_WAIT_MS = 700;

/**
 * #41 follow up (Fran, 15 Sep 21:48: "a weird shiny effect" in the hair on a computer monitor). A
 * browser squeezes a <video> into its box with a plain bilinear sample, no low pass, so a 1080 px
 * clip in a 300 px desktop card (3.6x) aliases fine strands into shimmer and colour fringes; measured
 * offline in media/results/alias-check-t41.json. So each clip also exists at 720 and 360 px, made
 * properly smaller, and the card plays the smallest one at least as wide as it is on screen in device
 * pixels: the browser then never squeezes by more than 2x. On a slow connection, one step smaller.
 */
export const RENDITIONS = [360, 720, 1080];

/** navigator.connection says the phone is on a slow line (Chrome and Android; iOS has no such API). */
export function isSlow(connection) {
  if (!connection) return false;
  if (connection.saveData) return true;
  if (connection.effectiveType && connection.effectiveType !== '4g') return true;
  return typeof connection.downlink === 'number' && connection.downlink > 0 && connection.downlink < SLOW_MBPS;
}
/** Below this reported downlink (Mbit/s) a phone gets the next smaller clip. Chrome reports at most 10. */
export const SLOW_MBPS = 6;

/** The rendition width a card of `cssWidth` CSS px at `dpr` should play. */
export function pickRendition(cssWidth, dpr, slow = false) {
  const need = Math.max(1, cssWidth) * Math.max(1, dpr || 1);
  let k = RENDITIONS.findIndex((w) => w >= need);
  if (k === -1) k = RENDITIONS.length - 1;
  if (slow && k > 0) k -= 1;
  return RENDITIONS[k];
}

/**
 * Whether the active clip may start now. Never while the strip is moving: measured on MAIN, the
 * clip started 180 to 260 ms BEFORE its card reached the middle, so the poster swapped to the
 * clip's first frame mid slide. It is prepared (src set, fetching) at the press, and started at rest.
 */
export function shouldStart({ start, inView, pausedByVisitor, moving }) {
  return start === 'autoplay' && Boolean(inView) && !pausedByVisitor && !moving;
}

/** The fewest cards the strip loops with: below this there is no card to show on both sides. */
export const LOOP_MIN = 3;

/** i wrapped into 0 .. n-1, both ways: -1 is the last card, n is the first. */
export function wrapIndex(i, n) {
  return ((i % n) + n) % n;
}

/**
 * The flex `order` of every card so that card `centre` sits in the middle slot, the cards before
 * it (wrapping) on its left and the cards after it on its right. For twelve cards and card 1:
 * 8 9 10 11 12 [1] 2 3 4 5 6 7.
 */
export function slotsFor(centre, n) {
  const mid = Math.floor((n - 1) / 2);
  return Array.from({ length: n }, (_, i) => wrapIndex(i - centre + mid, n));
}

/** The card an arrow press goes to: wrapping when the strip loops, stopping at the ends when not. */
export function stepIndex(index, delta, n, loop) {
  if (loop) return wrapIndex(index + delta, n);
  return Math.min(n - 1, Math.max(0, index + delta));
}

/** A slot this close to either end of the row has too few cards beyond it to scroll to smoothly. */
export function nearRowEnd(slot, n) {
  return slot < 2 || slot > n - 3;
}

/** How far ahead of the card on screen an arrow target may run before the strip jumps instead. */
export const MAX_AHEAD = 3;

class ResultStrip {
  constructor(root) {
    this.root = root;
    this.layout = root.dataset.layout === 'video' ? 'video' : 'pictures';
    this.start = root.dataset.start === 'tap' || REDUCED_MOTION.matches ? 'tap' : 'autoplay';
    // The stylesheet reads the start mode to decide whether the middle card shows a play icon.
    if (this.layout === 'video') root.dataset.start = this.start;
    this.strip = root.querySelector('[data-pb-rs-strip]');
    this.cards = Array.from(root.querySelectorAll('[data-pb-rs-card]'));
    this.prev = root.querySelector('[data-pb-rs-prev]');
    this.next = root.querySelector('[data-pb-rs-next]');
    this.counter = root.querySelector('[data-pb-rs-current]');
    this.loop = this.cards.length >= LOOP_MIN;
    this.slots = null;
    this.index = 0;
    // The card an arrow press is scrolling to. While it is set, a scroll that comes to rest
    // anywhere else is a scroll still on its way, not a new position (see `settle`).
    this.pending = null;
    this.pausedByVisitor = false;
    // #41: true from an arrow press until the strip is at rest. While it is set the new clip is
    // fetched but not started, and the outgoing clip fades out on its own last frame.
    this.moving = false;
    this.moveTimer = null;
    this.releaseTimers = new Map();
    // Assume NOT in view where an observer can tell us: starting from `true` fetched a whole clip
    // on every page load in the first round, before the observer's first callback aborted it.
    this.inView = !('IntersectionObserver' in window);

    if (!this.strip || this.cards.length === 0) return;

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.strip.addEventListener('keydown', (e) => this.onKeydown(e));

    // A swipe or a trackpad scroll is the other way the visitor moves the strip. Whichever card
    // it comes to rest on becomes the active one, and it takes over from any arrow press still
    // in flight.
    const takeOver = () => {
      this.pending = null;
    };
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
      this.strip.addEventListener(type, takeOver, { passive: true })
    );
    // While a finger is on the strip it is never re-arranged under it. Without `scrollend`
    // (older Safari) a finger held still mid-drag stops the scroll events, and the settle timer
    // would otherwise fire during the touch. So settling waits for the finger to lift.
    this.touching = false;
    this.strip.addEventListener('touchstart', () => (this.touching = true), { passive: true });
    ['touchend', 'touchcancel'].forEach((type) =>
      this.strip.addEventListener(
        type,
        () => {
          this.touching = false;
          if (!HAS_SCROLLEND) this.onScroll();
        },
        { passive: true }
      )
    );
    if (HAS_SCROLLEND) {
      this.strip.addEventListener('scrollend', () => this.settle());
    } else {
      this.strip.addEventListener('scroll', () => this.onScroll(), { passive: true });
    }

    if (this.layout === 'video') {
      this.cards.forEach((card, i) => {
        card.querySelector('[data-pb-rs-play]')?.addEventListener('click', () => this.onPlayClick(i));
        const video = this.video(i);
        if (!video) return;
        // Show the video only once it has a frame to show, so the poster never flashes to black.
        video.addEventListener('playing', () => this.setState(i, 'playing'));
        video.addEventListener('ended', () => this.onEnded(i));
      });

      // Nothing plays while the strip is off screen: it saves the visitor's data and battery.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (!this.inView) this.stopAll();
            else this.sync();
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this.root);
      }
    }

    // On load the first card is the active one and sits in the middle, the last card on its left
    // when the strip loops. Put it there without an animation, in case the browser restored an old
    // scroll position.
    this.arrange(0);
    this.strip.scrollTo({ left: this.targetFor(0), behavior: 'instant' });
    this.render();
    if (this.layout === 'video') this.sync();
  }

  /* ---- position ------------------------------------------------------------------ */

  /** A card's left edge in the strip's own scroll coordinates, whatever the page layout. */
  leftOf(card) {
    return card.getBoundingClientRect().left - this.strip.getBoundingClientRect().left + this.strip.scrollLeft;
  }

  /** The scrollLeft that puts card i in the middle of the strip. */
  targetFor(i) {
    const card = this.cards[i];
    const max = this.strip.scrollWidth - this.strip.clientWidth;
    const left = this.leftOf(card) + card.offsetWidth / 2 - this.strip.clientWidth / 2;
    return Math.min(max, Math.max(0, left));
  }

  /** The card whose middle is nearest the strip's middle. */
  centreIndex() {
    const mid = this.strip.scrollLeft + this.strip.clientWidth / 2;
    let best = 0;
    let bestGap = Infinity;
    this.cards.forEach((card, i) => {
      const gap = Math.abs(this.leftOf(card) + card.offsetWidth / 2 - mid);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    return best;
  }

  /**
   * The loop: give every card the `order` that puts card `centre` in the middle slot, then move
   * scrollLeft by exactly what the card now in the middle of the strip moved, so what is on screen
   * stays where it is. Only cards far off screen change sides.
   */
  arrange(centre) {
    if (!this.loop) return;
    const slots = slotsFor(centre, this.cards.length);
    if (this.slots && slots.every((s, i) => s === this.slots[i])) return;
    const middleCard = this.cards[this.centreIndex()];
    const before = middleCard.getBoundingClientRect().left;
    this.cards.forEach((card, i) => {
      card.style.order = String(slots[i]);
    });
    this.slots = slots;
    const shift = middleCard.getBoundingClientRect().left - before;
    if (Math.abs(shift) > 0.5) {
      this.strip.scrollTo({ left: this.strip.scrollLeft + shift, behavior: 'instant' });
    }
  }

  scrollToIndex(i, { instant = false } = {}) {
    this.strip.scrollTo({
      left: this.targetFor(i),
      behavior: instant || REDUCED_MOTION.matches ? 'instant' : 'smooth',
    });
  }

  /** The arrows. Exactly one card per press, and the counter changes with it. */
  step(delta) {
    const n = this.cards.length;
    const i = stepIndex(this.index, delta, n, this.loop);
    if (i === this.index) return;
    // Presses faster than the scroll (an arrow key held down repeats) walk the target ahead of
    // the strip before it has come to rest and been re-arranged. Near an end of the row, re-arrange
    // around the target first; once the target runs more than MAX_AHEAD cards ahead of the card on
    // screen, go there without an animation rather than sweep the row past.
    let instant = false;
    if (this.loop && this.slots) {
      instant = Math.abs(this.slots[i] - this.slots[this.centreIndex()]) > MAX_AHEAD;
      if (instant || nearRowEnd(this.slots[i], n)) this.arrange(i);
    }
    this.pending = i;
    this.startMoving(Math.abs(this.strip.scrollLeft - this.targetFor(i)) > ARRIVED_PX);
    this.scrollToIndex(i, { instant });
    this.setIndex(i);
  }

  /**
   * #41: an arrow press sends the strip on its way. Until it is at rest the new clip is only
   * fetched, not started (`shouldStart`). A press that does not scroll at all starts at once, and
   * a failsafe starts the clip if the browser never reports the end of the scroll.
   */
  startMoving(willScroll) {
    if (this.moveTimer) clearTimeout(this.moveTimer);
    this.moveTimer = null;
    this.moving = willScroll;
    if (willScroll) this.moveTimer = setTimeout(() => this.onFailsafe(), START_FAILSAFE_MS);
  }

  /**
   * No end of scroll was reported in time. Never start a clip under a finger: wait for it to lift.
   * Then drop the arrow target and settle on whatever card is really in the middle, so the counter,
   * the active card and the playing clip agree with what is on screen (from the #41 code review).
   */
  onFailsafe() {
    this.moveTimer = null;
    if (!this.moving) return;
    if (this.touching) {
      this.moveTimer = setTimeout(() => this.onFailsafe(), START_FAILSAFE_MS);
      return;
    }
    this.pending = null;
    this.settle();
  }

  onScroll() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settle(), SETTLE_MS);
  }

  /**
   * The strip has come to rest: the card in the middle is the active one. Except while an arrow
   * press is still travelling. A second press made mid-scroll interrupts the first, and the
   * browser reports that as a scroll that ended — on whatever card it happened to be passing.
   * Measured before this guard: three presses 120 ms apart read "2, 3, 4, 2, 4" on the counter.
   * So an unfinished arrow scroll is finished instead of believed.
   * At rest, the loop is re-arranged around the active card, so both sides are full again.
   */
  settle() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.touching) return; // settled on touchend instead
    if (this.pending !== null) {
      if (Math.abs(this.strip.scrollLeft - this.targetFor(this.pending)) > ARRIVED_PX) {
        this.scrollToIndex(this.pending);
        return;
      }
      this.pending = null;
    }
    const i = this.centreIndex();
    // #41: the strip is at rest. Re-arrange the loop first (it moves only cards far off screen),
    // then start the clip, so the clip's first frames never share a frame with the arranging.
    const wasMoving = this.moving;
    if (this.moveTimer) clearTimeout(this.moveTimer);
    this.moveTimer = null;
    this.moving = false;
    this.arrange(i);
    if (i !== this.index) this.setIndex(i);
    else if (wasMoving && this.layout === 'video') this.sync();
  }

  setIndex(i) {
    this.index = i;
    this.pausedByVisitor = false;
    this.render();
    if (this.layout === 'video') this.sync();
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    }
  }

  render() {
    if (this.counter) this.counter.textContent = String(this.index + 1);
    // A looping strip has no ends, so its arrows are never disabled.
    if (this.prev) this.prev.disabled = !this.loop && this.index === 0;
    if (this.next) this.next.disabled = !this.loop && this.index === this.cards.length - 1;
    this.cards.forEach((card, i) => {
      card.toggleAttribute('data-active', i === this.index);
      this.renderButton(i);
    });
  }

  /* ---- playback, prototype D only -------------------------------------------------- */

  video(i) {
    return this.cards[i]?.querySelector('[data-pb-rs-video]') || null;
  }

  /**
   * A card is idle, playing, or paused by the visitor. Only `playing` shows the video; idle and
   * paused both show the after-frame poster, so the frame a visitor pauses on — which can be
   * from the before — never stands in for the result.
   */
  setState(i, state) {
    const card = this.cards[i];
    if (!card) return;
    card.toggleAttribute('data-playing', state === 'playing');
    card.toggleAttribute('data-paused', state === 'paused');
    this.renderButton(i);
  }

  /**
   * Only the active card's button can be reached. The others are taken out of the tab order and
   * out of the pointer's way, so a click on a card at the side lands on a plain figure: nothing
   * focuses, so the browser has no reason to scroll anything into view.
   */
  renderButton(i) {
    const card = this.cards[i];
    const button = card?.querySelector('[data-pb-rs-play]');
    if (!button) return;
    const active = i === this.index;
    button.tabIndex = active ? 0 : -1;
    button.setAttribute('aria-hidden', active ? 'false' : 'true');
    const label = card.hasAttribute('data-playing') ? button.dataset.pauseLabel : button.dataset.playLabel;
    if (label) button.setAttribute('aria-label', label);
  }

  /**
   * Give a clip its src, so it starts fetching. #41: this happens at the arrow press, while the
   * strip is still travelling; the clip is started only once the strip is at rest (`sync`). A card
   * whose fade out is still running keeps its src, so arrowing straight back costs no refetch.
   */
  prepare(i) {
    const video = this.video(i);
    if (!video) return null;
    this.cancelRelease(i);
    if (!video.getAttribute('src') && video.dataset.src) {
      video.preload = 'auto';
      video.setAttribute('src', this.sourceFor(i, video));
    }
    return video;
  }

  /** #41: the rendition this card should play: its width on screen x devicePixelRatio, and the line. */
  sourceFor(i, video) {
    const width = this.cards[i].getBoundingClientRect().width;
    const dpr = HAS_WINDOW ? window.devicePixelRatio || 1 : 1;
    const slow = isSlow(HAS_WINDOW ? navigator.connection : null);
    const w = pickRendition(width, dpr, slow);
    // data-src-720 is dataset['src-720'] (a dash before a digit is not camel-cased)
    const src = w === 1080 ? video.dataset.src : video.dataset[`src-${w}`];
    return src || video.dataset.src;
  }

  /**
   * Start a clip. #41: once the browser says it can play through (or after READY_WAIT_MS), so on a
   * slow network the after-frame poster stays a moment longer instead of a clip that starts and
   * then stalls. A tap (`now`) starts at once. A later press makes an earlier, still waiting start
   * a no-op.
   */
  play(i, { now = false } = {}) {
    const video = this.prepare(i);
    if (!video) return;
    video.muted = true; // required for autoplay, and the clips have no audio track at all
    video.loop = this.start === 'autoplay';
    const token = (this.playToken = (this.playToken || 0) + 1);
    const go = () => {
      // A later press, the strip leaving the screen (`stopAll` releases the src) or a release
      // while waiting all make this start stale: starting it then would fail and mark the card
      // as paused by the visitor, so it would not autoplay again when it came back into view.
      if (token !== this.playToken || i !== this.index || !video.getAttribute('src')) return;
      if (!now && (!this.inView || this.moving)) return; // at rest, `settle` starts it again
      const started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch((error) => {
          // An AbortError is our own doing: the arrows moved on and `stop` paused or unloaded the
          // clip before it started. Anything else is the browser refusing autoplay (an iOS
          // low-power mode will), and then the poster stays with the play button on it — but only
          // on the card that is still the active one.
          if (error?.name === 'AbortError' || i !== this.index) return;
          this.pausedByVisitor = true;
          this.setState(i, 'paused');
        });
      }
    };
    if (now || video.readyState >= 4) {
      go();
      return;
    }
    let done = false;
    const ready = () => {
      if (done) return;
      done = true;
      video.removeEventListener('canplaythrough', ready);
      clearTimeout(timer);
      go();
    };
    video.addEventListener('canplaythrough', ready);
    const timer = setTimeout(ready, READY_WAIT_MS);
  }

  /**
   * Stop a clip. `release` also drops the src, which is what keeps "only the playing clip is
   * fetched" true: a paused video keeps its buffered bytes and its connection.
   * #41: with `fade`, the clip is paused and the section fades it out on its own last frame; the
   * src goes RELEASE_AFTER_MS later, unless the card has become the active one again. Before #41
   * the src went at once, which blanked the moving card to its poster within 10 ms of the press.
   */
  stop(i, { release, fade = false }) {
    const video = this.video(i);
    if (!video) return;
    // Only a clip that was on screen needs the fade. One that never played (a card the arrows only
    // passed) shows its poster already, so its download is dropped at once: a held arrow key must
    // not leave a queue of 1080p clips fetching (from the #41 code review).
    const shown = this.cards[i].hasAttribute('data-playing');
    video.pause();
    this.setState(i, 'idle');
    if (!release || !video.getAttribute('src')) return;
    if (!fade || !shown) {
      this.releaseNow(i);
      return;
    }
    if (this.releaseTimers.has(i)) return;
    this.releaseTimers.set(
      i,
      setTimeout(() => {
        this.releaseTimers.delete(i);
        if (i !== this.index) this.releaseNow(i);
      }, RELEASE_AFTER_MS)
    );
  }

  releaseNow(i) {
    this.cancelRelease(i);
    const video = this.video(i);
    if (!video || !video.getAttribute('src')) return;
    video.removeAttribute('src');
    video.load();
  }

  cancelRelease(i) {
    const timer = this.releaseTimers.get(i);
    if (timer) clearTimeout(timer);
    this.releaseTimers.delete(i);
  }

  stopAll() {
    this.playToken = (this.playToken || 0) + 1; // #41: cancels a start still waiting to play through
    this.cards.forEach((_, i) => this.stop(i, { release: true }));
  }

  /**
   * Every card but the active one is stopped and released (#41: faded out first). The active one
   * is prepared at once, so it is fetching while the strip travels, and started only at rest.
   */
  sync() {
    this.cards.forEach((_, i) => {
      if (i !== this.index) this.stop(i, { release: true, fade: true });
    });
    if (this.start !== 'autoplay' || !this.inView || this.pausedByVisitor) return;
    this.prepare(this.index);
    const state = { start: this.start, inView: this.inView, pausedByVisitor: this.pausedByVisitor, moving: this.moving };
    if (shouldStart(state)) this.play(this.index);
  }

  /** A tap plays or pauses the active card. It never scrolls, and a side card ignores it. */
  onPlayClick(i) {
    if (i !== this.index) return;
    const video = this.video(i);
    if (!video) return;
    if (!video.paused) {
      video.pause();
      this.pausedByVisitor = true;
      this.setState(i, 'paused');
      return;
    }
    this.pausedByVisitor = false;
    this.play(i, { now: true });
  }

  /**
   * A clip that ends stays on its card. In tap mode it goes back to its poster, which is the
   * after frame; with autoplay it loops, and this only runs if the browser dropped the loop.
   */
  onEnded(i) {
    if (this.start === 'autoplay' && i === this.index && this.inView && !this.pausedByVisitor) {
      const video = this.video(i);
      if (video) {
        video.currentTime = 0;
        this.play(i);
      }
      return;
    }
    this.stop(i, { release: false });
  }
}

function init() {
  document.querySelectorAll('[data-pb-rs]').forEach((root) => {
    if (root.dataset.pbRsReady) return;
    root.dataset.pbRsReady = '1';
    new ResultStrip(root);
  });
}

// The pure helpers above are imported by a node test (shopify-admin/_test-result-strip-loop-*),
// where there is no page to bind to.
if (typeof document !== 'undefined') {
  init();
  // The theme editor re-renders a section in place, so re-bind when it does.
  document.addEventListener('shopify:section:load', init);
}
